import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import {
  Box,
  ButtonCore,
  ButtonIcon,
  Icon,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { NODE_DATA_MODEL } from '../../../models/CertsModels';
import {
  consultLineItem,
  decommissionEntitlement,
  getVarifyEOParentItemStatus,
  getVarifyParentItemStatus,
  postApprove,
  postApproveUser,
  postChangeEndDate,
  postRejectAction,
  postTerminateUser,
  reassignLineItem,
  resetReassignLineItem,
  setTabHightLightKey,
} from '../../../redux/certdetails/action';
import {
  SET_ACTION_LOADING,
  SET_IS_REASSIGN_POPOVER_MSG,
} from '../../../redux/certdetails/types';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import { getMessageKey } from '../../../utils/utils';
import CertsTileActionsDrawer from '../../CertsDetailsList/Tile/CertsTileActionsDrawer/CertsTileActionsDrawer';
import {
  CERT_ENTITLEMENT_VALUEKEY,
  CERT_EO_CAMPAIGN_ROW_TYPE,
  CERT_TILE_TYPE,
} from '../../CertsDetailsList/Tile/constant';
import {
  EO_ENT_CERT_ENTITLEMENT_VALUEKEY,
  TAB_STATUS_ALL,
  TAB_STATUS_CONS,
  TAB_STATUS_DONE,
  TAB_STATUS_TODO,
} from '../../ViewControls';
import ActionButton from '../CertsActionButton/CertsActionButton';
import { enableReassignPopover } from '../CertsActionButton/utils';
import CertsSidePanelCommentDrawer from '../CertsSidePanelCommentDrawer/CertsSidePanelCommentDrawer';
import {
  ACTION_BUTTON_GROUP_KINDS,
  ACTION_BUTTON_KINDS,
  CERTIFIED,
  ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES,
  ENABLE_INDIVIDUAL_COMMENTS_FOR_USER,
  EO_CERTIFIED,
} from '../constant';
import getActionStates from '../helpers/getActionStates';

import { ROLE_TYPES } from './constants';
import { invertedMessages } from './messages';
import {
  getApprovePayload,
  getConditionalCertifiyPayload,
  getConsultPayload,
  getReassignPayload,
  getRejectPayload,
  getTerminateOrDecommissionPayload,
} from './RequestPayloadUtils';
import {
  getCommentRequiredForEOCampaign,
  getToolTipForConsult,
  trackActionLoading,
  trackActionSelect,
} from './utils';

import './CertsActionButtonsGroup.css';

const TOOLTIP_DELAY_IN_MS_TILE = 1000;
const TOOLTIP_DELAY_IN_MS_SIDEPANEL = 0;

const prioritizeButtons = [
  ACTION_BUTTON_KINDS.WORKS_FOR_ME, // Confirm Manager
  ACTION_BUTTON_KINDS.APPROVE, // Approve child
  ACTION_BUTTON_KINDS.BELONGS_TO_ME, // Approve for SA campaign step1
  ACTION_BUTTON_KINDS.DECOMMISSION,
  ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME, // Reassign User
  ACTION_BUTTON_KINDS.REJECT, // Reject Child
];

const actionPriority = [
  ACTION_BUTTON_KINDS.CONSULT, // Consult
  ACTION_BUTTON_KINDS.EXTEND_END_DATE, // Change End Date User
  ACTION_BUTTON_KINDS.BELONGS_TO_ME, // Approve for SA campaign step1
  ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY, // Change End Date child
  ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME, // Reassign for SA campaign step1
  ACTION_BUTTON_KINDS.REASSIGN, // Reassign Child
  ACTION_BUTTON_KINDS.TERMINATE, // Terminate User
];

function CertsActionButtonsGroup({
  kind,
  nodeData,
  filteredNodeData,
  actionsData,
  handleAutoSelectParent,
  isGroupDisabled,
  groupTooltipText,
  isActionGroupExpanded,
  setIsActionGroupExpanded,
  setActionPopoverOpenId,
  setMergedLists,
  isSaviResponded,
  saviRespondedReason,
  isScrolling,
  getTileCheckIcon,
  titleValue,
  getTileKindHeading,
  handleTileClick,
  bulkSelectInfoForDrawer,
  dataTestId,
}) {
  // Side panel action comments
  const [isSidePanelCommentDrawerOpen, setIsSidePanelCommentDrawerOpen] =
    useState(false);
  const [drawerActionKind, setDrawerActionKind] = useState(null);
  const [isSidePanelLoadingKind, setIsSidePanelLoadingKind] = useState('');
  const [drawerActionSelected, setDrawerActionSelected] = useState(false);
  const [orderedButtons, setOrderedButtons] = useState([]);
  const [isTileActionDrawerOpen, setIsTileActionDrawerOpen] = useState(false);
  const [isActionDrawerOutsideClick, setIsActionDrawerOutsideClick] =
    useState(true);

  const expandedHoverTimerRef = useRef(null);
  const kabobRef = useRef(null);
  const actionBtnRefs = useRef([]);
  const dispatch = useDispatch();
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const initializeRefs = (action) => {
    actionBtnRefs.current[action] = React.createRef();
  };

  // fetching redux store values for action items
  const {
    core: { configMap: getViewApiConfigmap, campaignType },
    global: {
      backNavigation,
      certKey,
      globalDateFormat,
      isTouchEnabled,
      mediaQueryValue,
    },
    lineActions: {
      certificationStartDate,
      enableComments,
      errorMessage: isActionErrorMessage,
      isActionError,
      isActionLoading,
      isActionSuccess,
      isAtLeastThreeActions,
      isPopoverUserReassignMsgShown,
      reassignConfigMap,
    },
    progressBreakdowndetails: { byDecisionDetails },
    sidePanel: { isSidePanelVisible },
    tabs: { tabData },
  } = useSelector((state) => state?.certifications?.details);
  const {
    users: enableCommentsUsers,
    accEntRole: enableCommentsAccEntRole,
    accounts: enableIndividualCommentsForAccounts,
    entitlements: enableIndividualCommentsForEntitlements,
    childEntilements: enableIndividualCommentsForChildEnt,
  } = enableComments;
  const getFirstTimeReject = !(
    byDecisionDetails?.Summary && byDecisionDetails?.Summary?.Rejected
  );
  const activeTab = tabData?.find((tab) => tab?.isActive);
  const isDetailsGroup = kind === ACTION_BUTTON_GROUP_KINDS.TILE;
  const isFooterGroup = kind === ACTION_BUTTON_GROUP_KINDS.FOOTER;
  const nodeDataForGroup = actionsData || nodeData;

  // fetching each line item values from /items api
  const accountKey = nodeData?.CERT_ACCOUNTKEY;
  const userKey = nodeData?.CERT_USERKEY;
  const entValueKey = nodeData?.CERT_ENTITLEMENT_VALUEKEY;
  const eoRowType = nodeData?.ROW_TYPE?.toString();
  const roleKey = nodeData?.CERT_ROLEKEY;
  const trackerId = nodeData?.tracker?.id;
  const actionTakenByPrimaryCertifier = nodeData?.actionTakenByPrimaryCertifier;
  const actionButtonsMap =
    nodeData?.availableAction && Object.keys(nodeData?.availableAction);
  const disableButtonsMap = nodeDataForGroup?.enableAction;
  const mobileXLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileXLargeQuery.value <= mediaQueryValue;
  const MIN_BUTTONS_FOR_KABOB = isMobileXlargeWidth && !isFooterGroup ? 2 : 3;

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const isAOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

  const l = useInvertedLocalize(invertedMessages);

  const APPROVE_TEXT = l('Approve');
  const CANCEL_APPROVE_TEXT = l('Cancel Approval');
  const CONFIRM_MANAGER_TEXT = l('Confirm Manager');
  const CANCEL_CONFIRM_MANAGER_TEXT = l('Cancel Manager Confirmation');

  const getOrderedButtons = () => {
    const selectedActions = [];

    if (nodeDataForGroup?.tracker?.actions?.consult?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.CONSULT);
    if (nodeDataForGroup?.tracker?.actions?.extendEndDate?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.EXTEND_END_DATE);
    if (nodeDataForGroup?.tracker?.actions?.conditionalCertifiy?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY);
    if (nodeDataForGroup?.tracker?.actions?.reassign?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.REASSIGN);
    if (nodeDataForGroup?.tracker?.actions?.terminated?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.TERMINATE);
    if (nodeDataForGroup?.tracker?.actions?.belongsToMe?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.BELONGS_TO_ME);
    if (nodeDataForGroup?.tracker?.actions?.doesNotBelongToMe?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME);
    if (nodeDataForGroup?.tracker?.actions?.decommission?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.DECOMMISSION);
    if (nodeDataForGroup?.tracker?.actions?.edit?.isSelected)
      selectedActions.push(ACTION_BUTTON_KINDS.EDIT);

    const orderedSelectedActions = actionPriority.filter((key) =>
      selectedActions.includes(key)
    );
    const prioritizedButtons = prioritizeButtons.filter((key) =>
      actionButtonsMap.includes(key)
    );
    // Combine prioritized with selected
    const selectedAndPrioritized = [
      ...prioritizedButtons,
      ...orderedSelectedActions,
    ];

    const remainingButtons = actionButtonsMap.filter(
      (key) => !selectedAndPrioritized.includes(key)
    );

    return [...selectedAndPrioritized, ...remainingButtons];
  };

  useEffect(() => {
    setOrderedButtons(getOrderedButtons());
  }, [nodeDataForGroup]);

  useEffect(() => {
    if (orderedButtons?.length > 0) {
      orderedButtons.forEach((ele) => {
        initializeRefs(ele);
      });
    }
  }, [orderedButtons]);

  useEffect(() => {
    if (isTileActionDrawerOpen && !isMobileXlargeWidth)
      setIsTileActionDrawerOpen(false);
  }, [isMobileXlargeWidth]);

  useEffect(() => {
    if (!isActionLoading) setIsSidePanelLoadingKind('');
  }, [isActionLoading]);

  const getTypeActEntRole = () => {
    if (isEOCampaign) {
      if (eoRowType === CERT_EO_CAMPAIGN_ROW_TYPE.ENTITLEMENT)
        return CERT_TILE_TYPE.ENTITLEMENT;

      if (eoRowType === CERT_EO_CAMPAIGN_ROW_TYPE.ACCOUNT)
        return CERT_TILE_TYPE.ACCOUNT;

      if (eoRowType === CERT_EO_CAMPAIGN_ROW_TYPE.CHILD_ENTITLEMENT)
        return CERT_TILE_TYPE.CHILD_ENTITLEMENT;

      return CERT_TILE_TYPE.ENTITLEMENT;
    }

    if (entValueKey === CERT_ENTITLEMENT_VALUEKEY.ROLE)
      return CERT_TILE_TYPE.ROLE;

    if (entValueKey === CERT_ENTITLEMENT_VALUEKEY.ACCOUNT)
      return CERT_TILE_TYPE.ACCOUNT;

    if (entValueKey === CERT_ENTITLEMENT_VALUEKEY.USER)
      return CERT_TILE_TYPE.USER;

    return CERT_TILE_TYPE.ENTITLEMENT;
  };

  // Destructure Action States using helper function
  const {
    isApprove,
    isApproveDisabled,
    isApproveLoading,
    isReassign,
    isReassignDisabled,
    isReassignLoading,
    isConsult,
    isConsultLoading,
    isConsultDisabled,
    isWorksForMe,
    isWorksForMeLoading,
    isWorksForMeDisabled,
    isReject,
    isRejectDisabled,
    isRejectLoading,
    isTerminated,
    isTerminatedDisabled,
    isTerminatedLoading,
    isDoesNotWorksForMe,
    isDoesNotWorksForMeLoading,
    isDoesNotWorksForMeDisabled,
    isDoesNotBelongToMe,
    isDoesNotBelongToMeLoading,
    isDoesNotBelongToMeDisabled,
    isExtendEndDate,
    isExtendEndDateLoading,
    isExtendEndDateDisabled,
    isConditionalCertifiy,
    isConditionalCertifiyLoading,
    isConditionalCertifiyDisabled,
    isEdit,
    isEditLoading,
    isEditDisabled,
    isDecommission,
    isDecommissionLoading,
    isDecommissiondDisabled,
  } = getActionStates(nodeDataForGroup);

  // Use these to set the tracker state of your actions
  const setIsActionLoading = (actionKind, isLoading) =>
    setMergedLists((prev) =>
      trackActionLoading(prev, actionKind, isLoading, trackerId)
    );

  const setIsActionSelected = (actionKind, isSelected) => {
    setMergedLists((prev) =>
      trackActionSelect(prev, actionKind, isSelected, trackerId)
    );
  };

  const dispatchActionHandler = (actionKind, status, dispatchFunc, payload) => {
    // Set Loading onClick
    setIsActionLoading(actionKind, true);
    setIsSidePanelLoadingKind(actionKind);
    setIsSidePanelCommentDrawerOpen(false);

    const currentState = !(
      status === null ||
      status === undefined ||
      status === false
    );

    dispatch(dispatchFunc(payload, actionKind))
      .then((response) => {
        // Close Kabob on success
        if (isTileActionDrawerOpen) setIsTileActionDrawerOpen(false);
        // reset tab highlight
        dispatch(setTabHightLightKey(null));

        if (response.isSuccess) {
          let tabToHighLight = '';

          if (
            (activeTab.key === TAB_STATUS_TODO ||
              activeTab.key === TAB_STATUS_CONS) &&
            !currentState
          ) {
            tabToHighLight = TAB_STATUS_DONE;
          }

          if (activeTab.key === TAB_STATUS_DONE && currentState) {
            tabToHighLight = nodeData?.isConsulted
              ? TAB_STATUS_CONS
              : TAB_STATUS_TODO;
          }

          if (
            (activeTab.key === TAB_STATUS_ALL ||
              activeTab.key === TAB_STATUS_TODO) &&
            actionKind === ACTION_BUTTON_KINDS.CONSULT
          ) {
            tabToHighLight = TAB_STATUS_CONS;
          }

          const flags = {
            shouldSwitchSidePanel: false,
            jumpToSameLevel: true,
          };

          if (
            actionKind === ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME ||
            actionKind === ACTION_BUTTON_KINDS.REASSIGN ||
            actionKind === ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME
          ) {
            if (
              (activeTab.key === TAB_STATUS_TODO && !currentState) ||
              (activeTab.key === TAB_STATUS_DONE && currentState)
            ) {
              flags.shouldSwitchSidePanel = true;
            }

            if (activeTab.key === TAB_STATUS_ALL && currentState) {
              flags.shouldSwitchSidePanel = false;
              flags.jumpToSameLevel = false;
            }

            if (activeTab.key === TAB_STATUS_ALL && !currentState) {
              flags.shouldSwitchSidePanel = true;
              flags.jumpToSameLevel = false;
            }
          } else {
            // START: Enter logic based on action here @padmavathi, @vishnu, @shaurabh, @jacob
            if (
              actionKind === ACTION_BUTTON_KINDS.REJECT ||
              actionKind === ACTION_BUTTON_KINDS.DECOMMISSION ||
              actionKind === ACTION_BUTTON_KINDS.TERMINATE ||
              actionKind === ACTION_BUTTON_KINDS.CONSULT ||
              actionKind === ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY
            ) {
              if (
                ((activeTab.key === TAB_STATUS_TODO ||
                  activeTab.key === TAB_STATUS_CONS) &&
                  !currentState) ||
                (activeTab.key === TAB_STATUS_DONE && currentState)
              ) {
                flags.shouldSwitchSidePanel = true;

                // when consult account and inclued associate items
                if (
                  actionKind === ACTION_BUTTON_KINDS.CONSULT &&
                  payload?.cert_entitlement_valuekey ===
                    CERT_ENTITLEMENT_VALUEKEY.ACCOUNT &&
                  !payload?.reassignAssociated
                ) {
                  flags.jumpToSameLevel = false;
                }
              }
            }

            // Logic for transitions for respective actions in respective tabs
            // Approve actions
            if (actionKind === ACTION_BUTTON_KINDS.APPROVE) {
              // For Account - in side panel
              if (nodeData?.CERT_ENTITLEMENT_VALUEKEY === '0') {
                // todo tab
                if (
                  (activeTab.key === TAB_STATUS_TODO ||
                    activeTab.key === TAB_STATUS_CONS) &&
                  !currentState
                ) {
                  // Should switch to next level - YES
                  flags.shouldSwitchSidePanel = true;
                  // Jump to same level - NO
                  flags.jumpToSameLevel = false;
                }

                // done tab
                if (activeTab.key === TAB_STATUS_DONE && currentState) {
                  // jump to same level - TDB
                  flags.shouldSwitchSidePanel = true;
                  flags.jumpToSameLevel = false;
                }

                // all tab
                if (activeTab.key === TAB_STATUS_ALL && currentState) {
                  // Should switch to next level - YES
                  flags.shouldSwitchSidePanel = true;
                }
              } else {
                // For Role and Entitlements - side panels
                // todo tab
                if (
                  (activeTab.key === TAB_STATUS_TODO ||
                    activeTab.key === TAB_STATUS_CONS) &&
                  !currentState
                ) {
                  // Should switch to next level - YES
                  flags.shouldSwitchSidePanel = true;
                  // jump to same level
                  flags.jumpToSameLevel = true;
                }

                // done tab
                if (activeTab.key === TAB_STATUS_DONE && currentState) {
                  // only reset case
                  // jump to same level
                  flags.shouldSwitchSidePanel = true;
                  flags.jumpToSameLevel = false;
                }

                // all tab
                if (activeTab.key === TAB_STATUS_ALL && currentState) {
                  // Should switch to next level - YES
                  flags.shouldSwitchSidePanel = true;
                }
              }
            }

            // Works for me
            if (
              actionKind === ACTION_BUTTON_KINDS.WORKS_FOR_ME ||
              actionKind === ACTION_BUTTON_KINDS.BELONGS_TO_ME ||
              actionKind === ACTION_BUTTON_KINDS.EXTEND_END_DATE
            ) {
              // todo tab
              if (
                (activeTab.key === TAB_STATUS_TODO ||
                  activeTab.key === TAB_STATUS_CONS) &&
                !currentState
              ) {
                // Should switch to next level - YES
                flags.shouldSwitchSidePanel = true;
                // jump to same level - NO
                flags.jumpToSameLevel = false;
              }

              // done tab
              if (activeTab.key === TAB_STATUS_DONE && !currentState) {
                // jump to same level - TDB
                flags.shouldSwitchSidePanel = true;
                flags.jumpToSameLevel = true;
              }

              // RESET in done tab for approve user
              if (activeTab.key === TAB_STATUS_DONE && status) {
                // jump to next side panel in open state
                flags.shouldSwitchSidePanel = true;
              }

              // all tab
              if (activeTab.key === TAB_STATUS_ALL && currentState) {
                // Should switch to next level - YES
                flags.shouldSwitchSidePanel = true;
              }
            }

            // END: Enter logic based on action here @padmavathi, @vishnu, @shaurabh, @jacob

            if (activeTab.key === TAB_STATUS_ALL && currentState) {
              flags.shouldSwitchSidePanel = false;
              flags.jumpToSameLevel = false;
            }

            if (activeTab.key === TAB_STATUS_ALL && !currentState) {
              flags.shouldSwitchSidePanel = true;
              flags.jumpToSameLevel = false;
            }
          }

          let shouldHighLightTab = false;

          // Tab highlight should be done after the animation (This should ideally be in finally block)
          if (
            actionKind === ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME ||
            actionKind === ACTION_BUTTON_KINDS.REASSIGN ||
            actionKind === ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME
          ) {
            // don't highlight done tab ,  reassign record to another user highlight not required
            // when assignee is not selected and userlevel skip certifier is selected don't show done tab highlight

            if (
              !payload?.assignee ||
              payload.status === CERTIFIED.REVOKED ||
              payload.status === '0'
            ) {
              shouldHighLightTab = true;
            }
          } else {
            shouldHighLightTab = true;
          }

          if (
            activeTab.key === TAB_STATUS_ALL &&
            (actionKind === ACTION_BUTTON_KINDS.TERMINATE ||
              actionKind === ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME)
          ) {
            flags.shouldSwitchSidePanel = false;
            flags.jumpToSameLevel = true;
          }

          // TODO: New function to handle auto Refresh list, Do not delete this comment
          handleAutoSelectParent(
            // referenceObject
            {
              trackerId: nodeData?.tracker?.id,
              trackerLevel: nodeData?.tracker?.level,
              actionKind,
              currentState,
              flags,
            },
            // nonReferenceObject
            {
              alertBannerPayload: response?.alertBannerPayload,
              tabToHighLightPayload: { shouldHighLightTab, tabToHighLight },
            }
          );

          // if (!flags.jumpToSameLevel) {
          //   flags.shouldSwitchSidePanel = false; // MSG: true or false based on your requirements
          // }
        }

        if (isFooterGroup) setIsActionSelected(actionKind, !currentState);
      })
      .catch(() => {
        setIsActionLoading(actionKind, false);
        setIsSidePanelLoadingKind('');
        setIsActionSelected(actionKind, currentState);
      })
      .finally(() => {
        setIsActionLoading(actionKind, false);
        setIsSidePanelLoadingKind('');
      });
  };

  const actionButtonWrapperClasses = cx(
    'CertsActionButtonsGroup-wrapper',
    isActionGroupExpanded && 'CertsActionButtonsGroup-wrapper--isExpanded'
  );
  const actionButtonGroupClasses = cx(
    'CertsActionButtonsGroup',
    actionButtonsMap?.length &&
      `CertsActionButtonsGroup--length${actionButtonsMap.length}`,
    isAtLeastThreeActions && 'CertsActionButtonsGroup--isAtLeastThreeActions'
  );
  const getKindClasses = (isDrawerActions) =>
    cx(
      isDetailsGroup &&
        !isDrawerActions &&
        'CertsActionButtonsGroup-actionsOnly',
      isFooterGroup && 'CertsActionButtonsGroup-sidePanelFooter'
    );

  // Reset drawerActionKind
  useEffect(() => {
    if (!isSidePanelCommentDrawerOpen) setDrawerActionKind(null);
  }, [isSidePanelCommentDrawerOpen]);

  // Functions completed
  const handleApprove = (commentValue, isModalDrawerVersion = false) => {
    setIsActionDrawerOutsideClick(true);
    if (isModalDrawerVersion) setIsSidePanelCommentDrawerOpen(false);

    let isCommentRequired =
      enableCommentsAccEntRole?.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      ) ||
      (enableIndividualCommentsForAccounts?.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.BELONGS_TO_ME
      ) &&
        CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT);

    if (isEOCampaign) {
      switch (eoRowType) {
        case CERT_EO_CAMPAIGN_ROW_TYPE.ACCOUNT:
          isCommentRequired = enableIndividualCommentsForAccounts.includes(
            ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
          );
          break;
        case CERT_EO_CAMPAIGN_ROW_TYPE.CHILD_ENTITLEMENT:
          isCommentRequired = enableIndividualCommentsForChildEnt.includes(
            ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
          );
          break;
        default:
          break;
      }
    }

    const approvePayload = getApprovePayload(
      isEOCampaign,
      isApprove,
      certKey,
      nodeData,
      commentValue
    );

    const dispatchApprove = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.APPROVE,
        isApprove,
        postApprove,
        approvePayload
      );

    setActionPopoverOpenId(false);

    // Tile on click
    if (isDetailsGroup) dispatchApprove();

    // Footer on click
    if (isFooterGroup) {
      if (!isApprove && isCommentRequired) {
        setDrawerActionKind(ACTION_BUTTON_KINDS.APPROVE);
        if (!isModalDrawerVersion) setIsSidePanelCommentDrawerOpen(true);
      }

      if (commentValue?.length >= 3) {
        setIsSidePanelCommentDrawerOpen(false);
        dispatchApprove();
      }

      // Reset in footer
      if (!isCommentRequired || (isCommentRequired && isApprove))
        dispatchApprove();
    }
  };

  // Handling Works For Me api dispatch actions
  const handleWorksForMe = (commentValue) => {
    setIsActionDrawerOutsideClick(true);
    let isCommentRequired = enableCommentsUsers.includes(
      ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.WORKS_FOR_ME
    );

    if (isEOCampaign && eoRowType === CERT_EO_CAMPAIGN_ROW_TYPE.ENTITLEMENT) {
      isCommentRequired = enableIndividualCommentsForEntitlements?.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
      );
    }

    const approveUserPayload = {
      certKey,
      userKey,
      entValueKey,
      status: isWorksForMe ? CERTIFIED.RESET : CERTIFIED.WORKS_FOR_ME,
    };

    if (isEOCampaign) {
      approveUserPayload.status = isWorksForMe
        ? EO_CERTIFIED.RESET
        : EO_CERTIFIED.WORKS_FOR_ME;
      approveUserPayload.tabletoupdate = '0';
    }

    if (commentValue && commentValue.length > 0) {
      if (!isWorksForMe) approveUserPayload.comment = commentValue;
    }

    if (isWorksForMe) approveUserPayload.comment = 'Action Cancelled';

    const dispatchWorksForMe = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.WORKS_FOR_ME,
        isWorksForMe,
        postApproveUser,
        approveUserPayload
      );

    // Tile on click
    if (isDetailsGroup) dispatchWorksForMe();

    // Footer on click
    if (isFooterGroup) {
      if (!isWorksForMe && isCommentRequired) {
        setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
        setDrawerActionKind(ACTION_BUTTON_KINDS.WORKS_FOR_ME);
      }

      if (commentValue?.length >= 3) dispatchWorksForMe();

      // Reset in footer
      if (!isCommentRequired || (isCommentRequired && isWorksForMe))
        dispatchWorksForMe();
    }
  };

  // Handling Terminate api dispatch actions
  const handleTerminate = (commentValue) => {
    setIsActionDrawerOutsideClick(true);
    const isCommentRequired = enableCommentsUsers.includes(
      ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.TERMINATED
    );

    const payload = getTerminateOrDecommissionPayload(
      isEOCampaign,
      isTerminated,
      certKey,
      nodeData,
      commentValue
    );

    const dispatchTerminate = () => {
      if (isEOCampaign) {
        return dispatchActionHandler(
          ACTION_BUTTON_KINDS.DECOMMISSION,
          isTerminated,
          decommissionEntitlement,
          payload
        );
      }

      return dispatchActionHandler(
        ACTION_BUTTON_KINDS.TERMINATE,
        isTerminated,
        postTerminateUser,
        payload
      );
    };

    // Tile on click
    if (isDetailsGroup) dispatchTerminate();

    // Footer on click
    if (isFooterGroup) {
      if (!isTerminated) {
        setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
        setDrawerActionKind(ACTION_BUTTON_KINDS.TERMINATE);
      }

      if (commentValue?.length >= 3) {
        dispatchTerminate();

        return;
      }

      // Reset in footer
      if (!isCommentRequired || (isCommentRequired && isTerminated)) {
        dispatchTerminate();
      }
    }
  };

  // Handling Change End Date api dispatch actions for User
  const handleExtendEndDate = (getDate, commentValue) => {
    setIsActionDrawerOutsideClick(true);
    const isCommentRequired = enableCommentsUsers.includes(
      ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.EXTEND_END_DATE
    );

    let currentDate = new Date();
    const dd = currentDate.getDate();
    const mm = currentDate.getMonth() + 1;
    const yyyy = currentDate.getFullYear();

    currentDate = `${mm < 10 ? `0${mm}` : mm}-${
      dd < 10 ? `0${dd}` : dd
    }-${yyyy}`;

    const payload = {
      certKey,
      userKey,
      status: CERTIFIED.WORKS_FOR_ME, // works for me and extend end date have same status - api call
      extendedEndDate: getDate && getDate.length > 0 ? getDate : currentDate,
    };

    if (commentValue && commentValue.length > 0) {
      payload.comments = commentValue;
    }

    const dispatchExtendEndDate = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.EXTEND_END_DATE,
        isExtendEndDate,
        postChangeEndDate,
        payload
      );

    // Tile on click
    if (isDetailsGroup) dispatchExtendEndDate();

    // Footer on click
    if (isFooterGroup) {
      if (!isExtendEndDate) {
        setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
        setDrawerActionKind(ACTION_BUTTON_KINDS.EXTEND_END_DATE);
      }

      if (commentValue?.length >= 3) {
        dispatchExtendEndDate();

        return;
      }

      // Reset in footer
      if (!isCommentRequired || (isCommentRequired && isExtendEndDate)) {
        dispatchExtendEndDate();
      }
    }
  };

  // Handling Reject Action api dispatch actions
  const handleRejectAction = (commentValue) => {
    setIsActionDrawerOutsideClick(true);
    let isCommentRequired = enableCommentsAccEntRole.includes(
      ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
    );

    if (isEOCampaign) {
      switch (eoRowType) {
        case CERT_EO_CAMPAIGN_ROW_TYPE.ACCOUNT:
          isCommentRequired = enableIndividualCommentsForAccounts.includes(
            ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
          );
          break;
        case CERT_EO_CAMPAIGN_ROW_TYPE.CHILD_ENTITLEMENT:
          isCommentRequired = enableIndividualCommentsForChildEnt.includes(
            ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
          );
          break;
        default:
          break;
      }
    }

    const rejectPayload = getRejectPayload(
      isEOCampaign,
      isReject,
      certKey,
      nodeData,
      commentValue
    );
    const dispatchReject = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.REJECT,
        isReject,
        postRejectAction,
        rejectPayload
      );

    // Tile on click
    if (isDetailsGroup) dispatchReject();

    // Footer on click
    if (isFooterGroup) {
      if (!isReject) {
        setIsSidePanelCommentDrawerOpen(true);
        setDrawerActionKind(ACTION_BUTTON_KINDS.REJECT);
        setDrawerActionSelected(isReject);
      }

      if (commentValue?.length >= 3) {
        dispatchReject();

        return;
      }

      // Reset in footer
      if (!isCommentRequired || (isCommentRequired && isReject)) {
        dispatchReject();
      }
    }
  };

  // Handling Decommission Action api dispatch actions
  const handleDecommissionAction = (commentValue) => {
    setIsActionDrawerOutsideClick(true);
    const isCommentRequired = enableCommentsAccEntRole?.includes(
      ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.DECOMMISSION
    );
    const payload = getTerminateOrDecommissionPayload(
      isEOCampaign,
      isDecommission,
      certKey,
      nodeData,
      commentValue
    );
    const dispatchDecommission = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.DECOMMISSION,
        isDecommission,
        decommissionEntitlement,
        payload
      );

    // Tile on click
    if (isDetailsGroup) dispatchDecommission();

    // Footer on click
    if (isFooterGroup) {
      if (!isDecommission) {
        setIsSidePanelCommentDrawerOpen(true);
        setDrawerActionKind(ACTION_BUTTON_KINDS.DECOMMISSION);
        setDrawerActionSelected(isDecommission);
      }

      if (commentValue?.length >= 3) {
        dispatchDecommission();

        return;
      }

      // Reset in footer
      if (!isCommentRequired || (isCommentRequired && isDecommission)) {
        dispatchDecommission();
      }
    }
  };

  // Placeholder  Funcitons Below
  const handleReassign = async (reassignPayload) => {
    setIsActionDrawerOutsideClick(true);
    const verifyPayload = {
      certKey,
      userKey,
      entValueKey,
      accountKey,
      roleKey,
    };

    const getNameByType = () => {
      if (getTypeActEntRole() === CERT_TILE_TYPE.ACCOUNT)
        return nodeData?.Account_DISPLAYNAME;

      if (getTypeActEntRole() === CERT_TILE_TYPE.ROLE)
        return nodeData?.Role_DISPLAYNAME;

      if (getTypeActEntRole() === CERT_TILE_TYPE.ENTITLEMENT)
        return nodeData?.Ent_DISPLAYNAME;

      return null;
    };

    const currentChildObject = {
      type: getTypeActEntRole().toLowerCase(),
      childname: getNameByType(),
    };

    if (!isEOCampaign) {
      try {
        dispatch({ type: SET_ACTION_LOADING, payload: kind });
        const response = await getVarifyParentItemStatus(
          dispatch,
          verifyPayload,
          currentChildObject
        );
        const { proceed } = response;

        if (Object.hasOwnProperty.call(response, 'proceed') && !proceed) {
          // Logger.log("Approve user and entitlement")
          /* Start --- Approve parent  */
          const approvePayload = {
            certKey,
            userKey,
            entValueKey,
            accountKey: nodeData?.CERT_ACCOUNTKEY,
            status: isApprove ? CERTIFIED.RESET : CERTIFIED.APPROVE,
            certRoleKey: nodeData?.CERT_ROLEKEY,
          };

          approvePayload.comment = l(
            'Auto-approved as the child item has been actioned.'
          );

          switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
            case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT:
              delete approvePayload?.accountKey;
              delete approvePayload?.certRoleKey;
              approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.USER; // value = -4
              break;
            case CERT_ENTITLEMENT_VALUEKEY.ROLE:
              if (nodeData?.Role_ROLETYPE === ROLE_TYPES?.APPLICATION_ROLE) {
                approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.ACCOUNT; // value 0
                approvePayload.certRoleKey = null;
              } else {
                approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.USER; // value = -4
                delete approvePayload?.certRoleKey;
              }

              break;
            default:
              if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0) {
                approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.ACCOUNT; // value 0
                approvePayload.certRoleKey = null;
              }

              break;
          }

          dispatch(postApprove(approvePayload));
          /* End --- Approve parent  */
        }
      } catch (error) {
        Logger.log('Error handling API call:', error.message);
      }
    } else if (isEOCampaign && eoRowType !== EO_ENT_CERT_ENTITLEMENT_VALUEKEY) {
      const verifyEOPayload = {
        certkey: certKey,
        cert_entitlement_valuekey: nodeData?.CERT_ENTITLEMENT_VALUEKEY,
      };

      try {
        dispatch({ type: SET_ACTION_LOADING, payload: kind });
        const response = await getVarifyEOParentItemStatus(
          dispatch,
          verifyEOPayload
        );
        const { proceed } = response;

        if (Object.hasOwnProperty.call(response, 'proceed') && !proceed) {
          // Logger.log("Approve user and entitlement")
          /* Start --- Approve parent  */
          const approvePayload = {
            certKey,
            entValueKey,
            accountKey: accountKey?.toString(),
            status: isApprove ? EO_CERTIFIED.RESET : EO_CERTIFIED.APPROVE,
            tabletoupdate: '0',
          };

          approvePayload.comment = l(
            'Auto-approved as the child item has been actioned.'
          );

          dispatch(postApprove(approvePayload));
          /* End --- Approve parent  */
        }
      } catch (error) {
        Logger.log('Error handling API call:', error.message);
      }
    }

    const reassignRequestPayload = getReassignPayload(
      isEOCampaign,
      certKey,
      nodeData,
      reassignPayload,
      reassignConfigMap
    );

    if (isDoesNotBelongToMe) {
      reassignRequestPayload.status = `${CERTIFIED.RESET}`;
    }

    const dispatchReassign = () =>
      dispatchActionHandler(
        isAOCampaign
          ? ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME
          : ACTION_BUTTON_KINDS.REASSIGN,
        isReassign || isDoesNotBelongToMe,
        isReassign || isDoesNotBelongToMe
          ? resetReassignLineItem
          : reassignLineItem,
        reassignRequestPayload
      );

    // Tile on click
    if (isDetailsGroup) dispatchReassign();

    // Footer on click
    if (isFooterGroup) {
      setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
      setDrawerActionKind(ACTION_BUTTON_KINDS.REASSIGN);
      dispatchReassign();
    }
  };

  // Handling Change End Date api dispatch actions for Account,Entitlement and Role
  const handleConditionalCertifiy = async (getDate, comment) => {
    setIsActionDrawerOutsideClick(true);
    const verifyPayload = {
      certKey,
      userKey,
      entValueKey,
      accountKey,
      roleKey,
    };

    const getNameByType = () => {
      if (getTypeActEntRole() === CERT_TILE_TYPE.ACCOUNT)
        return nodeData?.Account_DISPLAYNAME;

      if (getTypeActEntRole() === CERT_TILE_TYPE.ROLE)
        return nodeData?.Role_DISPLAYNAME;

      if (getTypeActEntRole() === CERT_TILE_TYPE.ENTITLEMENT)
        return nodeData?.Ent_DISPLAYNAME;

      return null;
    };

    const currentChildObject = {
      type: getTypeActEntRole().toLowerCase(),
      childname: getNameByType(),
    };

    let commentValue = 'Extend end date again';

    if (comment && comment.length > 0) {
      commentValue = comment;
    } else if (isConditionalCertifiy) {
      commentValue = 'Action Cancelled';
    }

    let currentDate = new Date();
    const dd = currentDate.getDate();
    const mm = currentDate.getMonth() + 1;
    const yyyy = currentDate.getFullYear();

    currentDate = `${mm < 10 ? `0${mm}` : mm}-${
      dd < 10 ? `0${dd}` : dd
    }-${yyyy}`;

    if (!isEOCampaign) {
      if (
        nodeData.CERT_ENTITLEMENT_VALUEKEY !== CERT_ENTITLEMENT_VALUEKEY.USER &&
        !isConditionalCertifiy
      ) {
        try {
          dispatch({ type: SET_ACTION_LOADING, payload: kind });
          const response = await getVarifyParentItemStatus(
            dispatch,
            verifyPayload,
            currentChildObject
          );

          const { proceed } = response;

          if (Object.hasOwnProperty.call(response, 'proceed') && !proceed) {
            // Logger.log("Approve user and entitlement")
            /* Start --- Approve parent  */

            const approvePayload = {
              certKey,
              userKey,
              entValueKey,
              accountKey: nodeData?.CERT_ACCOUNTKEY,
              status: isApprove ? CERTIFIED.RESET : CERTIFIED.APPROVE,
              certRoleKey: nodeData?.CERT_ROLEKEY,
            };

            approvePayload.comment = l(
              'Auto-approved as the child item has been actioned.'
            );

            switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
              case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT:
                delete approvePayload?.accountKey;
                delete approvePayload?.certRoleKey;
                approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.USER; // value = -4
                break;
              case CERT_ENTITLEMENT_VALUEKEY.ROLE:
                if (nodeData?.Role_ROLETYPE === ROLE_TYPES?.APPLICATION_ROLE) {
                  approvePayload.entValueKey =
                    CERT_ENTITLEMENT_VALUEKEY.ACCOUNT; // value 0
                  approvePayload.certRoleKey = null;
                } else {
                  approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.USER; // value = -4
                  delete approvePayload?.certRoleKey;
                }

                break;
              default:
                if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0) {
                  approvePayload.entValueKey =
                    CERT_ENTITLEMENT_VALUEKEY.ACCOUNT; // value 0
                  approvePayload.certRoleKey = null;
                }

                break;
            }

            await /** @type {Promise<any>} */ (
              dispatch(postApprove(approvePayload))
            );
            /* End --- Approve parent  */
          }
        } catch (error) {
          Logger.log('Error handling API call:', error.message);
        }
      }
    }

    const payload = getConditionalCertifiyPayload(
      isEOCampaign,
      isConditionalCertifiy,
      getDate,
      currentDate,
      certKey,
      nodeData,
      comment
    );
    const dispatchConditionalCertify = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY,
        isConditionalCertifiy,
        postChangeEndDate,
        payload
      );

    // Tile on click
    if (isDetailsGroup) dispatchConditionalCertify();

    // Footer on click
    if (isFooterGroup) {
      if (!isConditionalCertifiy) {
        setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
        setDrawerActionKind(ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY);
      }

      if (commentValue?.length >= 3) {
        dispatchConditionalCertify();
      }
    }
  };

  const handleConsultWithPeers = async (consultPayload) => {
    setIsActionDrawerOutsideClick(true);
    const verifyPayload = {
      certKey,
      userKey,
      entValueKey,
      accountKey,
      roleKey,
    };

    const getNameByType = () => {
      if (getTypeActEntRole() === CERT_TILE_TYPE.ACCOUNT)
        return nodeData?.Account_DISPLAYNAME;

      if (getTypeActEntRole() === CERT_TILE_TYPE.ROLE)
        return nodeData?.Role_DISPLAYNAME;

      if (getTypeActEntRole() === CERT_TILE_TYPE.ENTITLEMENT)
        return nodeData?.Ent_DISPLAYNAME;

      return null;
    };

    const currentChildObject = {
      type: getTypeActEntRole().toLowerCase(),
      childname: getNameByType(),
    };

    if (!isEOCampaign) {
      if (
        nodeData.CERT_ENTITLEMENT_VALUEKEY !== CERT_ENTITLEMENT_VALUEKEY.USER
      ) {
        try {
          dispatch({ type: SET_ACTION_LOADING, payload: kind });
          const response = await getVarifyParentItemStatus(
            dispatch,
            verifyPayload,
            currentChildObject
          );

          const { proceed } = response;

          if (Object.hasOwnProperty.call(response, 'proceed') && !proceed) {
            // Logger.log("Approve user and entitlement")
            /* Start --- Approve parent  */
            const approvePayload = {
              certKey,
              userKey,
              entValueKey,
              accountKey: nodeData?.CERT_ACCOUNTKEY,
              status: isApprove ? CERTIFIED.RESET : CERTIFIED.APPROVE,
              certRoleKey: nodeData?.CERT_ROLEKEY,
            };

            approvePayload.comment = l(
              'Auto-approved as the child item has been actioned.'
            );

            switch (nodeData?.CERT_ENTITLEMENT_VALUEKEY) {
              case CERT_ENTITLEMENT_VALUEKEY.ACCOUNT:
                delete approvePayload?.accountKey;
                delete approvePayload?.certRoleKey;
                approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.USER; // value = -4
                break;
              case CERT_ENTITLEMENT_VALUEKEY.ROLE:
                if (nodeData?.Role_ROLETYPE === ROLE_TYPES?.APPLICATION_ROLE) {
                  approvePayload.entValueKey =
                    CERT_ENTITLEMENT_VALUEKEY.ACCOUNT; // value 0
                  approvePayload.certRoleKey = null;
                } else {
                  approvePayload.entValueKey = CERT_ENTITLEMENT_VALUEKEY.USER; // value = -4
                  delete approvePayload?.certRoleKey;
                }

                break;
              default:
                if (CERT_ENTITLEMENT_VALUEKEY.ENTITLEMENT >= 0) {
                  approvePayload.entValueKey =
                    CERT_ENTITLEMENT_VALUEKEY.ACCOUNT; // value 0
                  approvePayload.certRoleKey = null;
                }

                break;
            }

            dispatch(postApprove(approvePayload));
            /* End --- Approve parent  */
          }
        } catch (error) {
          Logger.log('Error handling API call:', error.message);
        }
      }
    } else if (isEOCampaign && eoRowType !== EO_ENT_CERT_ENTITLEMENT_VALUEKEY) {
      const verifyEOPayload = {
        certkey: certKey,
        cert_entitlement_valuekey: nodeData?.CERT_ENTITLEMENT_VALUEKEY,
      };

      try {
        dispatch({ type: SET_ACTION_LOADING, payload: kind });
        const response = await getVarifyEOParentItemStatus(
          dispatch,
          verifyEOPayload
        );
        const { proceed } = response;

        if (Object.hasOwnProperty.call(response, 'proceed') && !proceed) {
          // Logger.log("Approve user and entitlement")
          /* Start --- Approve parent  */
          const approvePayload = {
            certKey,
            entValueKey,
            accountKey: accountKey?.toString(),
            status: isApprove ? EO_CERTIFIED.RESET : EO_CERTIFIED.APPROVE,
            tabletoupdate: '0',
          };

          approvePayload.comment = l(
            'Auto-approved as the child item has been actioned.'
          );

          dispatch(postApprove(approvePayload));
          /* End --- Approve parent  */
        }
      } catch (error) {
        Logger.log('Error handling API call:', error.message);
      }
    }

    // WIP When resetting the consult for lineitem
    // if (isConsult) {
    //   payload.status = '0';
    //   payload.comments = consultPayload?.commentValue
    //     ? consultPayload?.commentValue
    //     : 'Consult';
    // } else {
    //  }

    const consultRequestPayload = getConsultPayload(
      isEOCampaign,
      certKey,
      nodeData,
      consultPayload
    );
    const dispatchConsult = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.CONSULT,
        isConsult,
        consultLineItem,
        consultRequestPayload
      );

    // Tile on click
    if (isDetailsGroup) dispatchConsult();

    // Footer on click
    if (isFooterGroup) {
      setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
      setDrawerActionKind(ACTION_BUTTON_KINDS.CONSULT);
      dispatchConsult();
    }
  };

  const handleDoesNotWorksForMe = (reassignPayload) => {
    setIsActionDrawerOutsideClick(true);
    let payload = {
      certkey: certKey,
      cert_userkey: nodeData.CERT_USERKEY,
      cert_entitlement_valuekey: nodeData.CERT_ENTITLEMENT_VALUEKEY,
      requestOrigin: 'UI',
    };

    if (isEOCampaign && eoRowType === CERT_EO_CAMPAIGN_ROW_TYPE.ENTITLEMENT) {
      payload = {
        cert_entitlement_valuekey: nodeData.CERT_ENTITLEMENT_VALUEKEY,
        certkey: certKey,
        elasticId: nodeData?.id,
        parentAction: '1',
      };
    }

    if (
      reassignPayload?.commentValue &&
      reassignPayload?.commentValue?.length > 0
    ) {
      if (!isDoesNotWorksForMe)
        payload.comments = reassignPayload?.commentValue;
    }

    if (isDoesNotWorksForMe) payload.comments = 'Action Cancelled';

    //  When resetting the reassign for user lineitem
    if (isDoesNotWorksForMe) {
      if (isEOCampaign) {
        payload.status = `${EO_CERTIFIED.RESET}`;
        payload.tabletoupdate = EO_ENT_CERT_ENTITLEMENT_VALUEKEY;
      } else {
        payload.status = `${CERTIFIED.RESET}`;
      }
    } else if (
      !enableReassignPopover(reassignConfigMap) ||
      backNavigation?.consulteeLoggedIn
    ) {
      if (isEOCampaign) {
        // update status when reassign popup is disabled
        payload.status = `${EO_CERTIFIED.DOES_NOT_BELONG_TO_ME}`; // status should be 4
        payload.updateStatus = true;
        payload.tabletoupdate = EO_ENT_CERT_ENTITLEMENT_VALUEKEY;
      } else {
        // update status when reassign popup is disabled
        payload.status = `${CERTIFIED.REVOKED}`; // status should be 3
        payload.updateStatus = true;
      }
    }

    if (
      reassignPayload?.selectedUser &&
      !reassignPayload?.isSkipCertifierChecked
    ) {
      payload.assignee = reassignPayload?.selectedUser?.value;
      payload.hasNewEntitlementOwnerSelected = 'yes';
    } else if (reassignPayload?.isSkipCertifierChecked) {
      // when skip certifier is checked for user lineitem
      if (isEOCampaign) {
        // add any payload changes required for skip reassign
      } else {
        payload.status = `${CERTIFIED.REVOKED}`;
      }
    }

    // EO Campaign check the payload
    if (isEOCampaign) {
      if (payload?.assignee === reassignConfigMap?.defaultReassignee) {
        // eslint-disable-next-line no-param-reassign
        delete payload.assignee;
        delete payload.hasNewEntitlementOwnerSelected;
        payload.useDefaultReassignee = '1';
      } else if (payload.assignee) {
        payload.useDefaultReassignee = EO_ENT_CERT_ENTITLEMENT_VALUEKEY;
      }
    }

    const dispatchReassignDoesNotWorkForMe = () =>
      dispatchActionHandler(
        ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME,
        isDoesNotWorksForMe,
        isDoesNotWorksForMe ? resetReassignLineItem : reassignLineItem,
        payload
      );

    // Tile on click
    if (isDetailsGroup) dispatchReassignDoesNotWorkForMe();

    // Footer on click
    if (isFooterGroup) {
      setIsSidePanelCommentDrawerOpen(!isSidePanelCommentDrawerOpen);
      setDrawerActionKind(ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME);
      dispatchReassignDoesNotWorkForMe();
    }
  };

  const handleMouseEnter = () => {
    if (expandedHoverTimerRef.current) {
      clearTimeout(expandedHoverTimerRef.current);
      expandedHoverTimerRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    if (isActionGroupExpanded)
      expandedHoverTimerRef.current = setTimeout(() => {
        setIsActionGroupExpanded(false);
      }, 1000);
  };

  const handleKabobClick = () => {
    if (!isMobileXlargeWidth) {
      setIsActionGroupExpanded(!isActionGroupExpanded);

      return;
    }

    setIsTileActionDrawerOpen(true);
  };

  const tooltipPostion = isDetailsGroup ? 'bottom' : 'top';
  const tooltipDelay = isDetailsGroup
    ? TOOLTIP_DELAY_IN_MS_TILE
    : TOOLTIP_DELAY_IN_MS_SIDEPANEL;

  const getTooltipText = (
    defaultText,
    isDisabled,
    isSelected,
    isConsultAction
  ) => {
    if (groupTooltipText) return groupTooltipText;

    if (isDisabled) return '';

    if (!actionTakenByPrimaryCertifier && isSelected && !isConsultAction)
      return (
        messages.CERTS_REVIEW_DECISION ||
        localize(intl, getMessageKey('CERTS_REVIEW_DECISION'))
      );

    return defaultText;
  };

  const setReassignUserPopoverMsg = () => {
    dispatch({ type: SET_IS_REASSIGN_POPOVER_MSG });
  };

  let tooltipText;

  if (isWorksForMe) {
    tooltipText = isEOCampaign
      ? CANCEL_APPROVE_TEXT
      : CANCEL_CONFIRM_MANAGER_TEXT;
  } else {
    tooltipText = isEOCampaign ? APPROVE_TEXT : CONFIRM_MANAGER_TEXT;
  }

  const actionButtons = {
    approve: {
      id: ACTION_BUTTON_KINDS.APPROVE,
      kind: ACTION_BUTTON_KINDS.APPROVE,
      onClick: handleApprove,
      isSelected: isApprove,
      isLoading: isApproveLoading,
      isDisabled: isApproveDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          isApprove ? CANCEL_APPROVE_TEXT : APPROVE_TEXT,
          isApproveDisabled,
          isApprove
        ),
      },
    },
    belongsToMe: {
      id: ACTION_BUTTON_KINDS.BELONGS_TO_ME,
      kind: ACTION_BUTTON_KINDS.BELONGS_TO_ME,
      onClick: handleApprove,
      isSelected: isApprove,
      isLoading: isApproveLoading,
      isDisabled: isApproveDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          isApprove ? CANCEL_APPROVE_TEXT : APPROVE_TEXT,
          isApproveDisabled,
          isApprove
        ),
      },
    },
    reject: {
      id: ACTION_BUTTON_KINDS.REJECT,
      kind: ACTION_BUTTON_KINDS.REJECT,
      onClick: handleRejectAction,
      isSelected: isReject,
      isLoading: isRejectLoading,
      isDisabled: isRejectDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          isReject
            ? messages.CERTS_CANCEL_REJECTION ||
                localize(intl, getMessageKey('CERTS_CANCEL_REJECTION'))
            : messages['Reject.Default.label.ATTESTATIONS'] ||
                localize(
                  intl,
                  getMessageKey('Reject.Default.label.ATTESTATIONS')
                ),
          isRejectDisabled,
          isReject
        ),
      },
    },
    decommission: {
      id: ACTION_BUTTON_KINDS.DECOMMISSION,
      kind: ACTION_BUTTON_KINDS.DECOMMISSION,
      onClick: handleDecommissionAction,
      isSelected: isDecommission,
      isLoading: isDecommissionLoading,
      isDisabled: isDecommissiondDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          messages.CERTS_BULK_DECOMMISSION ||
            localize(intl, getMessageKey('CERTS_BULK_DECOMMISSION')),
          isDecommissiondDisabled,
          isDecommission
        ),
      },
    },
    terminated: {
      id: ACTION_BUTTON_KINDS.TERMINATE,
      kind: ACTION_BUTTON_KINDS.TERMINATE,
      onClick: handleTerminate,
      isSelected: isTerminated,
      isLoading: isTerminatedLoading,
      isDisabled: isTerminatedDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          isTerminated
            ? messages.CERTS_CANCEL_TERMINATION ||
                localize(intl, getMessageKey('CERTS_CANCEL_TERMINATION'))
            : messages['Terminate.items.label.ATTESTATIONS'] ||
                localize(
                  intl,
                  getMessageKey('Terminate.items.label.ATTESTATIONS')
                ),
          isTerminatedDisabled,
          isTerminated
        ),
      },
    },
    reassign: {
      id: ACTION_BUTTON_KINDS.REASSIGN,
      kind: ACTION_BUTTON_KINDS.REASSIGN,
      onClick: handleReassign,
      isSelected: isReassign,
      isLoading: isReassignLoading,
      isDisabled: isReassignDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          messages['REASSIGNSTEP2.ATTESTATIONS.label'] ||
            localize(intl, getMessageKey('REASSIGNSTEP2.ATTESTATIONS.label')),
          isReassignDisabled,
          isReassign
        ),
      },
    },
    doesNotBelongToMe: {
      id: ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME,
      kind: ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME,
      onClick: handleReassign,
      isSelected: isDoesNotBelongToMe,
      isLoading: isDoesNotBelongToMeLoading,
      isDisabled: isDoesNotBelongToMeDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          messages['REASSIGNSTEP2.ATTESTATIONS.label'] ||
            localize(intl, getMessageKey('REASSIGNSTEP2.ATTESTATIONS.label')),
          isReassignDisabled,
          isReassign
        ),
      },
    },
    consult: {
      id: ACTION_BUTTON_KINDS.CONSULT,
      kind: ACTION_BUTTON_KINDS.CONSULT,
      onClick: handleConsultWithPeers,
      isSelected: isConsult,
      isLoading: isConsultLoading,
      isDisabled: isConsultDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: nodeData?.isConsulted ? 'bottom' : tooltipPostion,
        text: getTooltipText(
          getToolTipForConsult(nodeData, messages, intl, localize),
          isConsultDisabled,
          isConsult,
          true
        ),
      },
    },
    worksForMe: {
      id: ACTION_BUTTON_KINDS.WORKS_FOR_ME,
      kind: isEOCampaign
        ? ACTION_BUTTON_KINDS.APPROVE
        : ACTION_BUTTON_KINDS.WORKS_FOR_ME,
      onClick: handleWorksForMe,
      isSelected: isWorksForMe,
      isLoading: isWorksForMeLoading,
      isDisabled: isWorksForMeDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(tooltipText, isWorksForMeDisabled, isWorksForMe),
      },
    },
    doesNotWorksForMe: {
      id: ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME,
      kind: ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME,
      onClick: handleDoesNotWorksForMe,
      isSelected: isDoesNotWorksForMe,
      isLoading: isDoesNotWorksForMeLoading,
      isDisabled: isDoesNotWorksForMeDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          messages['REASSIGNSTEP2.ATTESTATIONS.label'] ||
            localize(intl, getMessageKey('REASSIGNSTEP2.ATTESTATIONS.label')),
          isDoesNotWorksForMeDisabled,
          isDoesNotWorksForMe
        ),
      },
    },
    extendEndDate: {
      id: ACTION_BUTTON_KINDS.EXTEND_END_DATE,
      kind: ACTION_BUTTON_KINDS.EXTEND_END_DATE,
      onClick: handleExtendEndDate,
      isSelected: isExtendEndDate,
      isLoading: isExtendEndDateLoading,
      isDisabled: isExtendEndDateDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          messages.CERTS_CHANGE_END_DATE ||
            localize(intl, getMessageKey('CERTS_CHANGE_END_DATE')),
          isExtendEndDateDisabled,
          isExtendEndDate
        ),
      },
    },
    conditionalCertifiy: {
      id: ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY,
      kind: ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY,
      onClick: handleConditionalCertifiy,
      isSelected: isConditionalCertifiy,
      isLoading: isConditionalCertifiyLoading,
      isDisabled: isConditionalCertifiyDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          isConditionalCertifiy
            ? messages.CERTS_CANCEL_END_DATE_CHANGE ||
                localize(intl, getMessageKey('CERTS_CANCEL_END_DATE_CHANGE'))
            : messages['Change.end.date.default.label.ATTESTATIONS'] ||
                localize(
                  intl,
                  getMessageKey('Change.end.date.default.label.ATTESTATIONS')
                ),
          isConditionalCertifiyDisabled,
          isConditionalCertifiy
        ),
      },
    },
    edit: {
      id: ACTION_BUTTON_KINDS.EDIT,
      kind: ACTION_BUTTON_KINDS.EDIT,
      onClick: () => {
        // Logger.warn('WIP');
      },
      isSelected: isEdit,
      isLoading: isEditLoading,
      isDisabled: isEditDisabled,
      tooltipProperties: {
        openDelay: tooltipDelay,
        placement: tooltipPostion,
        text: getTooltipText(
          messages.CERTS_EDIT || localize(intl, getMessageKey('CERTS_EDIT')),
          isEditDisabled,
          isEdit
        ),
      },
    },
  };

  const handleSubmitFunctions = {
    [ACTION_BUTTON_KINDS.APPROVE]: handleApprove,
    [ACTION_BUTTON_KINDS.BELONGS_TO_ME]: handleApprove,
    [ACTION_BUTTON_KINDS.REJECT]: handleRejectAction,
    [ACTION_BUTTON_KINDS.TERMINATE]: handleTerminate,
    [ACTION_BUTTON_KINDS.REASSIGN]: handleReassign,
    [ACTION_BUTTON_KINDS.CONSULT]: handleConsultWithPeers,
    [ACTION_BUTTON_KINDS.WORKS_FOR_ME]: handleWorksForMe,
    [ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME]: handleDoesNotWorksForMe,
    [ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME]: handleReassign,
    [ACTION_BUTTON_KINDS.EXTEND_END_DATE]: handleExtendEndDate,
    [ACTION_BUTTON_KINDS.CONDITIONAL_CERTIFIY]: handleConditionalCertifiy,
    [ACTION_BUTTON_KINDS.DECOMMISSION]: handleDecommissionAction,
  };

  // close consult popover from side panel drawer
  const closeConsultPopover = (checkBoxSelected) => {
    if (actionBtnRefs.current[drawerActionKind]) {
      actionBtnRefs.current[drawerActionKind].current?.childFunction(
        checkBoxSelected
      );
    }
  };

  // returns boolean value for reject action
  const getRejectCommentRequired = () => {
    if (!isEOCampaign) {
      return enableCommentsAccEntRole.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      );
    }

    const commentMap = {
      [CERT_EO_CAMPAIGN_ROW_TYPE.ACCOUNT]: enableIndividualCommentsForAccounts,
      [CERT_EO_CAMPAIGN_ROW_TYPE.CHILD_ENTITLEMENT]:
        enableIndividualCommentsForChildEnt,
    };

    return (
      commentMap[eoRowType]?.includes(
        ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
      ) || false
    );
  };

  const getReusableActionsMap = (isDrawerActions) => (
    <Box
      tag='div'
      ariaLabel='Box'
      className={getKindClasses(isDrawerActions)}
      dataTestId={dataTestId}>
      {orderedButtons?.length > 0 &&
        orderedButtons
          ?.filter(
            (btn) => btn !== 'edit' // not showing edit for EO campaign
          )
          .map((buttonKey, index) => {
            const buttonConfig = actionButtons[buttonKey];
            const isNotTabbable =
              index >= MIN_BUTTONS_FOR_KABOB && !isActionGroupExpanded;

            const getIsCommentRequired = () => {
              // EO campaign
              if (isEOCampaign) {
                return getCommentRequiredForEOCampaign(
                  buttonConfig,
                  eoRowType,
                  enableIndividualCommentsForEntitlements,
                  enableIndividualCommentsForAccounts,
                  enableIndividualCommentsForChildEnt
                );
              }

              // // SA campaign Account level
              // // enableCommentsForAccounts
              if (
                CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT &&
                CERT_ENTITLEMENT_VALUEKEY.ACCOUNT === entValueKey
              ) {
                // Specific to step1 SA campaign Approve comment
                if (
                  buttonConfig.id === ACTION_BUTTON_KINDS.BELONGS_TO_ME ||
                  buttonConfig.id === ACTION_BUTTON_KINDS.APPROVE
                ) {
                  return enableIndividualCommentsForAccounts?.includes(
                    ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.BELONGS_TO_ME
                  );
                }

                // Specific to step1 SA campaign Reassign comment
                if (
                  buttonConfig.id === ACTION_BUTTON_KINDS.DOES_NOT_BELONG_TO_ME
                ) {
                  return enableIndividualCommentsForAccounts?.includes(
                    ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.DOES_NOT_BELONG
                  );
                }

                // Specific to step1 SA campaign Consult comment
                if (buttonConfig.id === ACTION_BUTTON_KINDS.CONSULT) {
                  return enableIndividualCommentsForAccounts?.includes(
                    ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGN
                  );
                }

                // Specific to step1 and step2 SA campaign Reassign Comments
                if (buttonConfig.id === ACTION_BUTTON_KINDS.REJECT) {
                  return enableCommentsAccEntRole?.includes(
                    ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
                  );
                }

                // // Specific to step1 and step2 SA campaign Consult Comments
                // if (
                //   buttonConfig.id === ACTION_BUTTON_KINDS.CONSULT &&
                //   !reassignConfigMap.enableAccessVerificationOnly &&
                //   !reassignConfigMap.enableEmploymentVerificationOnly
                // ) {
                //   return enableCommentsAccEntRole?.includes(
                //     ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGN
                //   );
                // }
              }

              // User
              if (
                CERT_ENTITLEMENT_VALUEKEY.USER === entValueKey &&
                ACTION_BUTTON_KINDS.WORKS_FOR_ME === buttonConfig.id
              )
                return enableCommentsUsers.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.WORKS_FOR_ME
                );

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER === entValueKey &&
                ACTION_BUTTON_KINDS.DOES_NOT_WORK_FOR_ME === buttonConfig.id
              ) {
                return enableCommentsUsers.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.DOES_NOT_WORK_FOR_ME
                );
              }

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER === entValueKey &&
                ACTION_BUTTON_KINDS.CONSULT === buttonConfig.id
              ) {
                return enableCommentsUsers.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.REASSIGN
                );
              }

              // Acounts, Entitilements, Roles
              if (
                CERT_ENTITLEMENT_VALUEKEY.USER !== entValueKey &&
                ACTION_BUTTON_KINDS.APPROVE === buttonConfig.id
              )
                return enableCommentsAccEntRole.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.APPROVE
                );

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER === entValueKey &&
                ACTION_BUTTON_KINDS.TERMINATE === buttonConfig.id
              )
                return enableCommentsUsers.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.TERMINATED
                );

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER !== entValueKey &&
                ACTION_BUTTON_KINDS.REASSIGN === buttonConfig.id
              ) {
                return enableCommentsAccEntRole.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGNSTEP2
                );
              }

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER === entValueKey &&
                ACTION_BUTTON_KINDS.EXTEND_END_DATE === buttonConfig.id
              )
                return enableCommentsUsers.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_USER.EXTEND_END_DATE
                );

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER !== entValueKey &&
                ACTION_BUTTON_KINDS.REJECT === buttonConfig.id
              )
                return enableCommentsAccEntRole.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REJECT
                );

              if (
                CERT_ENTITLEMENT_VALUEKEY.USER !== entValueKey &&
                ACTION_BUTTON_KINDS.CONSULT === buttonConfig.id
              ) {
                return enableCommentsAccEntRole.includes(
                  ENABLE_INDIVIDUAL_COMMENTS_FOR_ACC_ENT_ROLES.REASSIGN
                );
              }

              return false;
            };

            if (!buttonConfig) {
              Logger.warn(`No configuration found for button: ${buttonKey}`);

              return null;
            }

            const {
              id,
              kind: actionkind,
              onClick,
              isSelected,
              isLoading,
              isDisabled,
              tooltipProperties,
            } = buttonConfig;

            const loadingConditions =
              isLoading ||
              (isActionLoading && actionkind === isSidePanelLoadingKind);
            const hideToolTipAndNotTabbable =
              (isDetailsGroup || (isMobileXlargeWidth && isFooterGroup)) &&
              isNotTabbable;

            return (
              <ActionButton
                ref={actionBtnRefs.current[actionkind]}
                key={id}
                id={id}
                activeTab={activeTab?.key}
                setActionPopoverOpenId={setActionPopoverOpenId}
                setIsSidePanelCommentDrawerOpen={
                  setIsSidePanelCommentDrawerOpen
                }
                setDrawerActionSelected={setDrawerActionSelected}
                kind={actionkind}
                groupKind={kind}
                onClick={onClick}
                isSelected={isSelected}
                isLoading={loadingConditions}
                isDisabled={
                  isDisabled ||
                  isGroupDisabled ||
                  !disableButtonsMap?.join(',')?.includes(id) ||
                  (disableButtonsMap?.length === 0 &&
                    actionButtonsMap?.join(',')?.includes(id))
                }
                isToolTipDisabled={isTouchEnabled && !isGroupDisabled}
                tooltipProperties={tooltipProperties}
                tabIndex={hideToolTipAndNotTabbable ? -1 : 0}
                isDrawerActions={isDrawerActions}
                isCommentRequired={getIsCommentRequired()}
                isHidden={hideToolTipAndNotTabbable && !isDrawerActions}
                getViewApiConfigmap={getViewApiConfigmap}
                extendEndDateDaysLimit={
                  getViewApiConfigmap?.ExtendEndDateDaysLimit &&
                  getViewApiConfigmap?.ExtendEndDateDaysLimit?.length > 0
                    ? getViewApiConfigmap?.ExtendEndDateDaysLimit
                    : ''
                }
                certKey={certKey}
                nodeData={nodeData}
                isActionSuccess={isActionSuccess}
                isActionError={isActionError}
                isActionErrorMessage={isActionErrorMessage}
                userKey={userKey}
                getTypeActEntRole={getTypeActEntRole()}
                isSidePanelVisible={isSidePanelVisible}
                reassignConfigMap={reassignConfigMap}
                getFirstTimeReject={getFirstTimeReject}
                certificationStartDate={certificationStartDate?.split(' ')[0]}
                conditionalCertifyDaysLimit={
                  getViewApiConfigmap?.ConditionallyCertifyInDaysLimit &&
                  getViewApiConfigmap?.ConditionallyCertifyInDaysLimit?.length >
                    0
                    ? getViewApiConfigmap?.ConditionallyCertifyInDaysLimit
                    : ''
                }
                isPopoverUserReassignMsgShown={isPopoverUserReassignMsgShown}
                setReassignUserPopoverMsg={setReassignUserPopoverMsg}
                setDrawerActionKind={setDrawerActionKind}
                isSaviResponded={isSaviResponded}
                saviRespondedReason={saviRespondedReason}
                isSidePanelCommentDrawerOpen={isSidePanelCommentDrawerOpen}
                consulteeLoggedIn={backNavigation?.consulteeLoggedIn}
                actionTakenByPrimaryCertifier={actionTakenByPrimaryCertifier}
                isScrolling={isScrolling}
                globalDateFormat={globalDateFormat}
                isActionGroupExpanded={isActionGroupExpanded}
                filteredNodeData={filteredNodeData}
                isActionDrawerOutsideClick={isActionDrawerOutsideClick}
                setIsActionDrawerOutsideClick={setIsActionDrawerOutsideClick}
              />
            );
          })}
    </Box>
  );

  const shouldRenderKabobButtonIcon =
    actionButtonsMap &&
    actionButtonsMap?.length > 0 &&
    (actionButtonsMap?.length > MIN_BUTTONS_FOR_KABOB || isMobileXlargeWidth);

  if (isFooterGroup) {
    return (
      <Box tag='div' className='CertsActionButtonsGroup-footerGroup'>
        {getReusableActionsMap(false)}
        {shouldRenderKabobButtonIcon && isMobileXlargeWidth && (
          <Box className='CertsTabItem-sidePanelMobileFooter-moreButton'>
            <ButtonCore onClick={handleKabobClick} ariaLabel={l('More')}>
              <Icon kind='MoreHorizontal' size='medium' color='indigo-700' />
            </ButtonCore>
            <Box className='CertsTileActionsDrawer-item CertsTileActionsDrawer-sidepanelItem'>
              <Box className='MobileDrawerItem-primaryLabelWrapper'>
                <Typography
                  kind='label'
                  desktop='body4-bold-d'
                  mobile='body4-bold-m'
                  className={cx('CertsSidePanel-footerActions-moreButton')}>
                  {l('More')}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        <CertsSidePanelCommentDrawer
          isSidePanelCommentDrawerOpen={isSidePanelCommentDrawerOpen}
          setIsSidePanelCommentDrawerOpen={setIsSidePanelCommentDrawerOpen}
          setDrawerActionSelected={setDrawerActionSelected}
          drawerActionKind={drawerActionKind}
          onSubmit={handleSubmitFunctions[drawerActionKind] || (() => {})}
          getTypeActEntRole={getTypeActEntRole()}
          isRejectCommentRequired={getRejectCommentRequired()}
          setIsSidePanelLoadingKind={setIsSidePanelLoadingKind}
          nodeData={nodeData}
          globalDateFormat={globalDateFormat}
          certKey={certKey}
          drawerActionSelected={drawerActionSelected}
          isSaviResponded={isSaviResponded}
          consulteeLoggedIn={backNavigation?.consulteeLoggedIn}
          closeConsultPopover={closeConsultPopover}
          actionBtnRefs={actionBtnRefs}
          tooltipPosition={tooltipPostion}
          activeTab={activeTab?.key}
          isMobileXlargeWidth={isMobileXlargeWidth}
          isActionDrawerOutsideClick={isActionDrawerOutsideClick}
          setIsActionDrawerOutsideClick={setIsActionDrawerOutsideClick}
        />
        <span className='CertsActionButtonsGroup-overlay' />
        {isTileActionDrawerOpen && isMobileXlargeWidth && (
          <CertsTileActionsDrawer
            isActionDrawerOutsideClick={isActionDrawerOutsideClick}
            reusableActionsMap={getReusableActionsMap(true)}
            isTileActionDrawerOpen={isTileActionDrawerOpen}
            setIsTileActionDrawerOpen={setIsTileActionDrawerOpen}
            tileCheckIcon={getTileCheckIcon(true)}
            titleValue={titleValue}
            tileKindHeading={getTileKindHeading()}
            handleTileClick={handleTileClick}
            bulkSelectInfoForDrawer={bulkSelectInfoForDrawer}
            checkSidePanelMobileFooter
          />
        )}
      </Box>
    );
  }

  return (
    <div
      className={actionButtonWrapperClasses}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}>
      <Box tag='div' className={actionButtonGroupClasses}>
        {getReusableActionsMap(false)}
        {shouldRenderKabobButtonIcon && (
          <Box tag='div' className='CertsActionButtonsGroup-kabobWrapper'>
            <span
              role='button'
              aria-hidden='true'
              className='CertsActionButtonsGroup-mobileKabobTarget'
              onClick={(e) => {
                e.stopPropagation();
                kabobRef.current?.click();
              }}
            />
            <ButtonIcon
              ref={kabobRef}
              kind='ghost'
              size='small'
              ariaLabel='kebab'
              icon={
                <Icon
                  kind={isActionGroupExpanded ? 'ExpandRight' : 'MoreVertical'}
                />
              }
              onClick={handleKabobClick}
              className='CertsActionButtonsGroup-kabob'
            />
          </Box>
        )}
      </Box>
      {/* Overlay */}
      <span className='CertsActionButtonsGroup-overlay' />
      {isTileActionDrawerOpen && (
        <CertsTileActionsDrawer
          isActionDrawerOutsideClick={isActionDrawerOutsideClick}
          reusableActionsMap={getReusableActionsMap(true)}
          isTileActionDrawerOpen={isTileActionDrawerOpen}
          setIsTileActionDrawerOpen={setIsTileActionDrawerOpen}
          tileCheckIcon={getTileCheckIcon(true)}
          titleValue={titleValue}
          tileKindHeading={getTileKindHeading()}
          handleTileClick={handleTileClick}
          bulkSelectInfoForDrawer={bulkSelectInfoForDrawer}
          checkSidePanelMobileFooter={false}
        />
      )}
    </div>
  );
}

CertsActionButtonsGroup.defaultProps = {
  actionsData: null,
  groupTooltipText: null,
  isActionGroupExpanded: null,
  setIsActionGroupExpanded: () => {},
  setActionPopoverOpenId: () => {},
  saviRespondedReason: [],
  isScrolling: false,
  filteredNodeData: {},
  getTileCheckIcon: () => {},
  titleValue: null,
  getTileKindHeading: () => {},
  bulkSelectInfoForDrawer: {},
  dataTestId: null,
};

CertsActionButtonsGroup.propTypes = {
  kind: PropTypes.oneOf(Object.values(ACTION_BUTTON_GROUP_KINDS)).isRequired,
  isGroupDisabled: PropTypes.bool.isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  actionsData: PropTypes.shape(NODE_DATA_MODEL),
  handleAutoSelectParent: PropTypes.func.isRequired,
  groupTooltipText: PropTypes.string,
  isActionGroupExpanded: PropTypes.bool,
  setIsActionGroupExpanded: PropTypes.func,
  setActionPopoverOpenId: PropTypes.func,
  setMergedLists: PropTypes.func.isRequired,
  isSaviResponded: PropTypes.bool.isRequired,
  saviRespondedReason: PropTypes.arrayOf(PropTypes.string),
  isScrolling: PropTypes.bool,
  filteredNodeData: PropTypes.shape({}),
  getTileCheckIcon: PropTypes.func,
  titleValue: PropTypes.string,
  getTileKindHeading: PropTypes.func,
  handleTileClick: PropTypes.func.isRequired,
  bulkSelectInfoForDrawer: PropTypes.shape({}),
  dataTestId: PropTypes.string,
};

export default CertsActionButtonsGroup;
